/**
 * Dev-only component to visualize all Manual Classification states.
 * Not used in production — purely for development / design review.
 *
 * Renders DocumentPreviewPanel in all states and DocumentTable in all states
 * as isolated cards for visual QA.
 *
 * Usage:
 *   import { ManualClassificationPreview } from "./Processes/ManualClassification/__mocks__/ManualClassificationPreview";
 *   <ManualClassificationPreview />
 */
import { Card } from "@/components/ui/card";

import { DocumentPreviewPanel } from "../DocumentPreviewPanel";
import { DocumentTable } from "../DocumentTable";
import {
  ALL_PREVIEW_STATES,
  MOCK_TABLE_INITIAL,
  MOCK_TABLE_NO_SELECTION,
  MOCK_TABLE_RECLASSIFIED,
} from "./mockData";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const TABLE_STATES = [
  MOCK_TABLE_INITIAL,
  MOCK_TABLE_RECLASSIFIED,
  MOCK_TABLE_NO_SELECTION,
] as const;

export const ManualClassificationPreview = () => {
  return (
    <div className="mx-auto max-w-6xl space-y-12 p-8">
      {/* ----------------------------------------------------------------- */}
      {/* DocumentPreviewPanel states                                        */}
      {/* ----------------------------------------------------------------- */}
      <section>
        <h2 className="mb-4 text-lg font-semibold">DocumentPreviewPanel States</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {ALL_PREVIEW_STATES.map((state, i) => (
            <div key={i} className="space-y-2">
              <p className="text-xs font-medium text-foreground-muted">
                {state.description}
              </p>
              <Card className="h-[600px] overflow-hidden p-4">
                <DocumentPreviewPanel
                  {...state.props}
                  onRetry={noop}
                  onAccept={noop}
                />
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* DocumentTable states                                               */}
      {/* ----------------------------------------------------------------- */}
      <section>
        <h2 className="mb-4 text-lg font-semibold">DocumentTable States</h2>
        <div className="grid grid-cols-1 gap-6">
          {TABLE_STATES.map((state, i) => (
            <div key={i} className="space-y-2">
              <p className="text-xs font-medium text-foreground-muted">
                {state.description}
              </p>
              <Card className="overflow-hidden p-4">
                <DocumentTable
                  files={state.files}
                  selectedFilename={state.selectedFilename}
                  getClassification={(file) => file.classification}
                  selectedClassification={state.selectedClassification}
                  getPageRange={(file) => {
                    if (file.startPage != null && file.endPage != null) {
                      return file.startPage === file.endPage
                        ? `Page ${file.startPage}`
                        : `Page ${file.startPage}-${file.endPage}`;
                    }
                    return "";
                  }}
                  onFileSelect={noop}
                  onClassificationChange={noop}
                />
              </Card>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
